import { FontSpec } from "chart.js";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { useTheme } from "@mui/material";
import { formatPercent, splitByWord } from "@app/utils";
import { CarPdfBox } from "@app/components/PdfBox";
import { CarPdfChartLegend } from "@app/components/PdfChartLegend";
import { CarPdfChart, CarPdfChartProps } from "@app/components/PdfChart";
import { CarPortfolioAssetTableData } from "@app/pages/assetAllocation/usePortfolioGroupVersionData";
import { AssetClassLevel } from "@app/const";
import { getChartModuleSummaryData } from "@app/pages/assetAllocation/editPortfolioGroup/ChartModuleSummary";

interface PortfolioAnalysisSummaryPdfChartProps
  extends Omit<CarPdfChartProps, "type" | "options" | "data" | "plugins"> {
  moduleId: string;
  tableData: CarPortfolioAssetTableData;
  level: AssetClassLevel;
  data1Id: string;
  data2Id: string;
  isWhiteContext?: boolean;
}

export const PortfolioAnalysisSummaryPdfChart = ({
  moduleId,
  tableData,
  level,
  data1Id,
  data2Id,
  isWhiteContext,
  ...props
}: PortfolioAnalysisSummaryPdfChartProps) => {
  const theme = useTheme();

  const { datasets, yLabel, xLabels } = getChartModuleSummaryData({
    theme,
    moduleId,
    tableData,
    level,
    data1Id,
    data2Id,
    forPdf: true,
  });

  const font: Partial<FontSpec> = {
    size: 9,
    weight: 400,
    lineHeight: 1,
    family: theme.typography.fontFamily,
  };

  const titleFont: Partial<FontSpec> = {
    ...font,
    size: 12,
    weight: 600,
  };

  const isHorizontal = level === AssetClassLevel.level4;

  const categoryScaleKey = isHorizontal ? "y" : "x";
  const valueScaleKey = isHorizontal ? "x" : "y";

  return (
    <CarPdfBox>
      <CarPdfChart
        type="bar"
        data={{ datasets }}
        options={{
          indexAxis: isHorizontal ? "y" : "x",
          scales: {
            [categoryScaleKey]: {
              type: "category",
              ticks: {
                font,
                color: theme.palette.softBlack,
                padding: 0,
              },
              grid: {
                display: false,
              },
              labels: isHorizontal
                ? xLabels.map((l) => splitByWord(l, 20))
                : xLabels,
            },
            [valueScaleKey]: {
              min: 0,
              max: 100,
              ticks: {
                font,
                color: theme.palette.softBlack,
                padding: 0,
                callback: (value) =>
                  typeof value === "number" ? formatPercent(value) : value,
              },
              title: {
                display: true,
                text: yLabel,
                color: theme.palette.softBlack,
                font: titleFont,
              },
              grid: ChartUtils.getGridOptions({ theme }),
            },
          },
          plugins: {
            roundedBackground: ChartUtils.getRoundedBackgroundOptions({
              theme,
              isWhiteContext,
            }),
          },
        }}
        plugins={[ChartPlugins.roundedBackground]}
        {...props}
      />
      <CarPdfChartLegend
        style={{ marginTop: 16 }}
        items={datasets.map((i) => ({
          label: i.label ?? "",
          color: String(i.backgroundColor),
          isCircle: true,
        }))}
      />
    </CarPdfBox>
  );
};
